import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/CampaignDetail.css';
import '../styles/ResultModal.css';
import Header from '../components/Header';
import tuyenDungService from '../services/tuyenDungService';
import dotTuyenDungService from '../services/dotTuyenDungService';
import danhMucService from '../services/danhMucService';
import { transformApplicants, createCampaignFromApplicants, transformDotTuyenDung } from '../utils/dataTransformers';
import { useNotification } from '../hooks/useNotification';
import Notification from '../components/Notification';
import ConfirmDialog from '../components/ConfirmDialog';

const CampaignDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); 
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [daChamDiemIds, setDaChamDiemIds] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedApplicantResults, setSelectedApplicantResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [experiencePopup, setExperiencePopup] = useState(null);
  const [zoomAvatarUrl, setZoomAvatarUrl] = useState(null);

  useEffect(() => {

    if (user) {
      fetchData();
    }
  }, [id, location.key, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
 
      const maSo = user?.maSo || user?.username;

  
      const results = await Promise.allSettled([
        dotTuyenDungService.getById(id),
        tuyenDungService.getAll(),
        danhMucService.getTonGiao(),
        danhMucService.getTinhTrangHonNhan(),
        danhMucService.getTrangThaiTuyenDung(),
        danhMucService.getXepLoaiDaoTao(),

        maSo ? tuyenDungService.getDaChamDiemByMaSo(maSo, id) : Promise.resolve([]),
        tuyenDungService.getDiemTrungBinhTheoDot(id)
      ]);

      const [dotRes, dataRes, tonGiaosRes, tinhTrangHonNhansRes, trangThaiTuyenDungsRes, xepLoaiDaoTaosRes, daChamDiemRes, diemTBRes] = results;

      if (dataRes.status !== 'fulfilled') {
        throw dataRes.reason;
      }

      const tonGiaos = tonGiaosRes.status === 'fulfilled' ? tonGiaosRes.value : [];
      const tinhTrangHonNhans = tinhTrangHonNhansRes.status === 'fulfilled' ? tinhTrangHonNhansRes.value : [];
      const trangThaiTuyenDungs = trangThaiTuyenDungsRes.status === 'fulfilled' ? trangThaiTuyenDungsRes.value : [];
      const xepLoaiDaoTaos = xepLoaiDaoTaosRes.status === 'fulfilled' ? xepLoaiDaoTaosRes.value : [];
      const daChamDiemValue = daChamDiemRes.status === 'fulfilled' ? daChamDiemRes.value : [];
      const daChamDiemList = Array.isArray(daChamDiemValue) ? daChamDiemValue : (Array.isArray(daChamDiemValue?.data) ? daChamDiemValue.data : []);

      if (tonGiaosRes.status === 'rejected' || tinhTrangHonNhansRes.status === 'rejected' || trangThaiTuyenDungsRes.status === 'rejected' || xepLoaiDaoTaosRes.status === 'rejected') {
        console.warn('Lookup data fetch failed, using empty lists.');
      }

     
      setDaChamDiemIds(daChamDiemList);

      const data = dataRes.value;

      const lookupData = { tonGiaos, tinhTrangHonNhans, trangThaiTuyenDungs, xepLoaiDaoTaos };
      const transformed = transformApplicants(data, lookupData);
      const dotId = Number(id);
      const applicantsInDot = transformed.filter(applicant => {
        const applicantDotId = Number(applicant.dotTuyenDungId ?? applicant.idDotTuyenDung);
        return Number.isFinite(dotId) && Number.isFinite(applicantDotId) && applicantDotId === dotId;
      });


      const scoreResults = await Promise.allSettled(
        applicantsInDot.map(a => tuyenDungService.getKetQuaChamDiem(a.id, id))
      );

      const applicantsWithStatus = applicantsInDot.map((applicant, index) => {
        const res = scoreResults[index];
        let averageScore = null;
        if (res.status === 'fulfilled') {
          const valid = res.value.filter(r => r.diemSo != null && r.diemSo > 0);
          averageScore = valid.length > 0
            ? valid.reduce((sum, r) => sum + r.diemSo, 0) / valid.length
            : null;
        }
        return {
          ...applicant,
          daChamDiemByCurrentUser: daChamDiemList.includes(applicant.id),
          averageScore
        };
      });

      setApplicants(applicantsWithStatus);
      if (dotRes.status === 'fulfilled' && dotRes.value) {
        setCampaign(transformDotTuyenDung(dotRes.value, applicantsWithStatus));
      } else {
        setCampaign(createCampaignFromApplicants(applicantsWithStatus, id));
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = async (applicant, event) => {
    event.stopPropagation(); 

    setLoadingResults(true);
    setShowResultModal(true);

    try {
      const [results, updatedApplicantData] = await Promise.all([
        tuyenDungService.getKetQuaChamDiem(applicant.id, id),
        tuyenDungService.getById(applicant.id)
      ]);

      const validResults = results.filter(r => r.diemSo != null && r.diemSo > 0);
      const correctAverage = validResults.length > 0
        ? validResults.reduce((sum, r) => sum + r.diemSo, 0) / validResults.length
        : null;

      const updatedApplicant = {
        ...applicant,
        idTrangThaiTuyenDung: updatedApplicantData.idTrangThaiTuyenDung,
        averageScore: correctAverage
      };

      setApplicants(prev => prev.map(a =>
        a.id === applicant.id ? { ...a, averageScore: correctAverage } : a
      ));

      setSelectedApplicantResults({ applicant: updatedApplicant, results });
    } catch (error) {
      console.error('Lỗi khi lấy kết quả chấm điểm:', error);
      showError('Không thể tải kết quả chấm điểm');
      setShowResultModal(false);
    } finally {
      setLoadingResults(false);
    }
  };

  const handleKetLuan = async (idTuyenDung, idTrangThaiTuyenDung, tenTrangThai) => {
    try {
      await tuyenDungService.capNhatKetLuan(idTuyenDung, idTrangThaiTuyenDung);

      showSuccess(`Đã cập nhật kết luận: ${tenTrangThai}`);

      const [results, updatedApplicantData] = await Promise.all([
        tuyenDungService.getKetQuaChamDiem(idTuyenDung, id),
        tuyenDungService.getById(idTuyenDung)
      ]);

      const newIdTrangThaiTuyenDung = updatedApplicantData.idTrangThaiTuyenDung;

      setApplicants(prevApplicants =>
        prevApplicants.map(a =>
          a.id === idTuyenDung
            ? { ...a, idTrangThaiTuyenDung: newIdTrangThaiTuyenDung }
            : a
        )
      );

      if (selectedApplicantResults) {
        setSelectedApplicantResults(prev => ({
          ...prev,
          applicant: {
            ...prev.applicant,
            idTrangThaiTuyenDung: newIdTrangThaiTuyenDung
          },
          results: results || []
        }));
      }

    } catch (error) {
      console.error('Lỗi khi cập nhật kết luận:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Không thể cập nhật kết luận';
      showError(errorMsg);
    }
  };

  const handleToggleKhongDenPhongVan = async (applicant, event) => {
    event.stopPropagation();

    const isKhongDen = applicant.idTrangThaiTuyenDung === 6;
    const newStatus = isKhongDen ? 2 : 6;
    const confirmMessage = isKhongDen
      ? 'Khôi phục ứng viên về trạng thái "Chờ phỏng vấn"?'
      : 'Xác nhận ứng viên không đến phỏng vấn?';

    setConfirmDialog({
      message: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog(null);

        try {
        
          await tuyenDungService.capNhatTrangThai(applicant.id, newStatus);

          const successMessage = isKhongDen
            ? 'Đã khôi phục về "Chờ phỏng vấn"'
            : 'Đã cập nhật: Không đến phỏng vấn';
          showSuccess(successMessage);

          const updatedApplicantData = await tuyenDungService.getById(applicant.id);
          const newIdTrangThaiTuyenDung = updatedApplicantData.idTrangThaiTuyenDung;

          setApplicants(prevApplicants =>
            prevApplicants.map(a =>
              a.id === applicant.id
                ? {
                    ...a,
                    idTrangThaiTuyenDung: newIdTrangThaiTuyenDung,
                    // Khi reset về status 2, backend đã xóa HoiDong → cập nhật local state
                    daChamDiemByCurrentUser: newIdTrangThaiTuyenDung === 2 ? false : a.daChamDiemByCurrentUser
                  }
                : a
            )
          );

        } catch (error) {
          console.error('Lỗi khi cập nhật trạng thái:', error);
          const errorMsg = error.response?.data?.message || error.message || 'Không thể cập nhật trạng thái';
          showError(errorMsg);
        }
      },
      onCancel: () => {
        setConfirmDialog(null);
      }
    });
  };



  const POSITION_ORDER = ['bác sĩ', 'điều dưỡng', 'Hộ sinh', 'kỹ thuật viên', 'Dược sỹ', 'hành chính'];

  const getPositionRank = (position) => {
    const p = (position || '').toLowerCase();
    const idx = POSITION_ORDER.findIndex(keyword => p.includes(keyword));
    return idx === -1 ? POSITION_ORDER.length : idx;
  };

  const filteredApplicants = applicants.filter(applicant => {
    const matchSearch = applicant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       applicant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       applicant.position.toLowerCase().includes(searchTerm.toLowerCase());


    let matchStatus = true;
    if (statusFilter === 'pending') {

      matchStatus = (applicant.idTrangThaiTuyenDung === 2 && !applicant.daChamDiemByCurrentUser)
        || applicant.idTrangThaiTuyenDung === 6;
    } else if (statusFilter === 'interviewed') {
      matchStatus = applicant.daChamDiemByCurrentUser && applicant.idTrangThaiTuyenDung !== 6;
    }


    return matchSearch && matchStatus;
  }).sort((a, b) => getPositionRank(a.position) - getPositionRank(b.position));


  const getStatusBadge = (applicant) => {

    if (applicant.idTrangThaiTuyenDung === 3) {
      return <span className="badge badge-success">Đạt phỏng vấn</span>;
    }

    if (applicant.idTrangThaiTuyenDung === 4) {
      return <span className="badge badge-error">Không đạt phỏng vấn</span>;
    }

    if (applicant.idTrangThaiTuyenDung === 6) {
      return <span className="badge badge-error">Không đến phỏng vấn</span>;
    }

    if (applicant.daChamDiemByCurrentUser) {
      return <span className="badge badge-info">Đã phỏng vấn</span>;
    }

    return <span className="badge badge-warning">Chờ phỏng vấn</span>;
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedApplicants(filteredApplicants.map(a => a.id));
    } else {
      setSelectedApplicants([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedApplicants.includes(id)) {
      setSelectedApplicants(selectedApplicants.filter(a => a !== id));
    } else {
      setSelectedApplicants([...selectedApplicants, id]);
    }
  };

  const stats = {
    total: applicants.length,
    pending: applicants.filter(a => (a.idTrangThaiTuyenDung === 2 && !a.daChamDiemByCurrentUser) || a.idTrangThaiTuyenDung === 6).length,
    interviewed: applicants.filter(a => a.daChamDiemByCurrentUser && a.idTrangThaiTuyenDung !== 6).length,
    approved: applicants.filter(a => a.idTrangThaiTuyenDung === 3).length,
    rejected: applicants.filter(a => a.idTrangThaiTuyenDung === 4).length
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1rem' }}>
            <p style={{ color: '#dc2626' }}>{error}</p>
            <button onClick={fetchData} className="btn btn-primary">Thử lại</button>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="page-wrapper">

      <div className="app-background">
        <div className="floating-icons">
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 9h-2v2H9v-2H7v-2h2V7h2v2h2v2zm0-4V3.5L17.5 9H13z"/>
            </svg>
          </div>
        </div>
      </div>

      <div className="page-container">

        <Header title="Chi Tiết Đợt Tuyển Dụng" />

        <main className="page-main">

          <div className="breadcrumb">
            <button onClick={() => navigate('/trangchu')} className="breadcrumb-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Trang Chủ
            </button>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className="breadcrumb-current">{campaign.title}</span>
          </div>


          <div className="campaign-info-card">
            <div className="campaign-info-header">
              <div>
                <h2>{campaign.title}</h2>
                <p className="campaign-description">{campaign.description}</p>
              </div>
              <span className={`badge ${campaign.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
                {campaign.status === 'active' ? 'Đang diễn ra' : 'Đã kết thúc'}
              </span>
            </div>
            <div className="campaign-info-meta">
              <div className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>{campaign.startDate} - {campaign.endDate}</span>
              </div>
              <div className="meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{campaign.positions} vị trí tuyển dụng</span>
              </div>
            </div>
          </div>


          <div className="stats-row">
            <div className="stat-mini-card">
              <span className="stat-mini-number">{stats.total}</span>
              <span className="stat-mini-label">Tổng hồ sơ</span>
            </div>
            <div className="stat-mini-card warning">
              <span className="stat-mini-number">{stats.pending}</span>
              <span className="stat-mini-label">Chờ phỏng vấn</span>
            </div>
            <div className="stat-mini-card info">
              <span className="stat-mini-number">{stats.interviewed}</span>
              <span className="stat-mini-label">Đã phỏng vấn</span>
            </div>
            <div className="stat-mini-card success">
              <span className="stat-mini-number">{stats.approved}</span>
              <span className="stat-mini-label">Đạt</span>
            </div>
            <div className="stat-mini-card error">
              <span className="stat-mini-number">{stats.rejected}</span>
              <span className="stat-mini-label">Từ chối</span>
            </div>
          </div>


          <div className="applicants-section">
            <div className="section-header">
              <h3>Danh sách ứng viên</h3>
              <div className="section-actions">
                {selectedApplicants.length > 0 && (
                  <span className="selected-count">{selectedApplicants.length} đã chọn</span>
                )}
              </div>
            </div>

 
            <div className="filters-bar">
              <div className="search-box">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Tìm kiếm theo tên, email, vị trí..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-tabs">
                <button
                  className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('pending')}
                >
                  Chờ phỏng vấn ({stats.pending})
                </button>
                <button
                  className={`filter-tab ${statusFilter === 'interviewed' ? 'active' : ''}`}
                  onClick={() => setStatusFilter('interviewed')}
                >
                  Đã phỏng vấn ({stats.interviewed})
                </button>
              </div>
            </div>


            <div className="table-container">
              <table className="applicants-table">
                <thead>
                  <tr>
                    
                    <th>Ứng viên</th>
                    <th className="hide-mobile">Vị trí ứng tuyển</th>
                    <th className="hide-mobile">Kinh nghiệm</th>
                    <th className="hide-tablet">Ngày nộp</th>
                    <th>Điểm Trung Bình</th>
                    <th>Trạng thái</th>
                    <th className="th-actions">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplicants.map(applicant => (
                    <tr
                      key={applicant.id}
                      className={`clickable-row ${selectedApplicants.includes(applicant.id) ? 'selected' : ''} ${applicant.idTrangThaiTuyenDung === 7 ? 'row-disabled' : ''}`}
                      onClick={() => {
                   
                        if (applicant.idTrangThaiTuyenDung === 6) return;

                     
                        if (!applicant.daChamDiemByCurrentUser) {
                          navigate(`/campaign/${id}/applicant/${applicant.id}`);
                        }
                     
                      }}
                    >
                      <td>
                        <div className="applicant-info">
                          <div
                            className="applicant-avatar"
                            style={applicant.avatar ? { cursor: 'zoom-in' } : {}}
                            onClick={applicant.avatar ? (e) => { e.stopPropagation(); setZoomAvatarUrl(applicant.avatar); } : undefined}
                          >
                            {applicant.avatar ? (
                              <img src={applicant.avatar} alt={applicant.name} />
                            ) : (
                              applicant.name.charAt(0)
                            )}
                          </div>
                          <div className="applicant-details">
                            <span className="applicant-name">{applicant.name}</span>
                            <span className="applicant-email">{applicant.email}</span>
                            <span className="applicant-phone hide-desktop">{applicant.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="hide-mobile" data-label="Vị trí">
                        <div className="experience-cell">
                          <span className="experience-text experience-truncate">{applicant.position}</span>
                          {applicant.position && applicant.position.length > 20 && (
                            <button
                              className="btn-view-experience"
                              title={applicant.position}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExperiencePopup(applicant);
                              }}
                            >
                              &#x22EF;
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="hide-mobile" data-label="Kinh nghiệm">
                        <div className="experience-cell">
                          <span className="experience-text experience-truncate">{applicant.experience}</span>
                          <button
                            className="btn-view-experience"
                            title="Xem tổng quan"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExperiencePopup(applicant);
                            }}
                          >
                            &#x22EF;
                          </button>
                        </div>
                      </td>
                      <td className="hide-tablet" data-label="Ngày nộp">
                        <span className="date-text">{applicant.appliedDate}</span>
                      </td>
                      <td data-label="Điểm Trung Bình">
                        <span className="average-score">{applicant.averageScore?.toFixed(1) || ''}</span>
                      </td>
                      <td data-label="Trạng thái">
                        {getStatusBadge(applicant)}
                      </td>
                      <td className="td-actions" data-label="Thao tác" onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          {applicant.idTrangThaiTuyenDung === 6 ? (
                          
                            <button
                              className="action-btn edit"
                              title="Khôi phục về Chờ phỏng vấn"
                              onClick={(e) => handleToggleKhongDenPhongVan(applicant, e)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                <path d="M3 21v-5h5" />
                              </svg>
                            </button>
                          ) : applicant.idTrangThaiTuyenDung === 2 ? (
                    
                            <>
                              <button
                                className="action-btn edit"
                                title={applicant.daChamDiemByCurrentUser ? "Sửa điểm chấm" : "Chấm điểm"}
                                onClick={() => navigate(`/campaign/${id}/applicant/${applicant.id}`)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                className="action-btn view"
                                title="Xem kết quả chấm điểm"
                                onClick={(e) => handleViewResults(applicant, e)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              {!applicant.daChamDiemByCurrentUser && (
                                <button
                                  className="action-btn delete"
                                  title="Không đến phỏng vấn"
                                  onClick={(e) => handleToggleKhongDenPhongVan(applicant, e)}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </>
                          ) : (applicant.idTrangThaiTuyenDung === 3 || applicant.idTrangThaiTuyenDung === 4) ? (
                  
                            <button
                                className="action-btn view"
                                title="Xem kết quả chấm điểm"
                                onClick={(e) => handleViewResults(applicant, e)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                          ) : (
                  
                            <>
                              <button
                                className="action-btn edit"
                                title="Sửa điểm chấm"
                                onClick={() => navigate(`/campaign/${id}/applicant/${applicant.id}`)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                className="action-btn view"
                                title="Xem kết quả & Kết luận"
                                onClick={(e) => handleViewResults(applicant, e)}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredApplicants.length === 0 && (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <p>Không tìm thấy ứng viên nào</p>
                </div>
              )}
            </div>
          </div>
        </main>


        <footer className="page-footer">
          <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
          <p>Powered by Ban Chuyển Đổi Số - leanhquan™</p>
        </footer>
      </div>

   
      {zoomAvatarUrl && (
        <div
          className="avatar-zoom-overlay"
          onClick={() => setZoomAvatarUrl(null)}
        >
          <img src={zoomAvatarUrl} alt="Avatar" className="avatar-zoom-img" />
        </div>
      )}

      {showResultModal && (
        <div className="modal-overlay" onClick={() => setShowResultModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kết quả chấm điểm</h2>
              <button className="modal-close" onClick={() => setShowResultModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {loadingResults ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Đang tải kết quả...</p>
              </div>
            ) : selectedApplicantResults ? (
              <>
                <div className="modal-body">
                  <div className="applicant-summary">
                    <h3>{selectedApplicantResults.applicant.name}</h3>
                    <p>{selectedApplicantResults.applicant.position}</p>
                  </div>

                  <div className="results-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Người chấm điểm</th>
                          <th>Kết quả PV</th>
                          <th>Điểm tổng</th>
                          <th>Ghi chú</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedApplicantResults.results.map((result, index) => (
                          <tr key={index}>
                            <td>{result.hoVaTenNguoiCham || (result.maSo === (user?.maSo || user?.username) ? user?.hoVaTen : result.maSo) || result.maSo}</td>
                            <td>{result.ketQuaPV?.ten || 'N/A'}</td>
                            <td><strong>{result.diemSo || 0}</strong></td>
                            <td>{result.ghiChu || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {selectedApplicantResults.results.length === 0 && (
                      <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                        Chưa có kết quả chấm điểm nào
                      </p>
                    )}
                  </div>

                  {selectedApplicantResults.results.length > 0 && selectedApplicantResults.applicant.daChamDiemByCurrentUser && (
                    <div className="conclusion-section">
                      <h4>Kết luận cuối cùng</h4>
                      <div className="conclusion-status">
                        {selectedApplicantResults.applicant.idTrangThaiTuyenDung === 3 && (
                          <span className="badge badge-success" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            ✓ Đã kết luận: Đạt phỏng vấn
                          </span>
                        )}
                        {selectedApplicantResults.applicant.idTrangThaiTuyenDung === 4 && (
                          <span className="badge badge-error" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            ✗ Đã kết luận: Không đạt
                          </span>
                        )}
                        {selectedApplicantResults.applicant.idTrangThaiTuyenDung !== 3 &&
                         selectedApplicantResults.applicant.idTrangThaiTuyenDung !== 4 && (
                          <span className="badge badge-warning" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            Chưa có kết luận
                          </span>
                        )}
                      </div>

                      {(selectedApplicantResults.applicant.idTrangThaiTuyenDung === 3 ||
                        selectedApplicantResults.applicant.idTrangThaiTuyenDung === 4) ? (
                        <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', marginTop: '1rem' }}>
                          Ứng viên này đã được kết luận. Không thể thay đổi kết luận.
                        </p>
                      ) : (
                        <div className="conclusion-actions">
                          <button
                            className="btn btn-success"
                            onClick={() => {
                              setConfirmDialog({
                                message: `Xác nhận kết luận ứng viên "${selectedApplicantResults.applicant.name}" ĐẠT phỏng vấn?\n\nSau khi xác nhận, kết luận này sẽ không thể thay đổi.`,
                                onConfirm: () => {
                                  setConfirmDialog(null);
                                  handleKetLuan(
                                    selectedApplicantResults.applicant.id,
                                    3,
                                    'Đạt phỏng vấn'
                                  );
                                },
                                onCancel: () => {
                                  setConfirmDialog(null);
                                }
                              });
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            Kết luận: Đạt
                          </button>

                          <button
                            className="btn btn-error"
                            onClick={() => {
                              setConfirmDialog({
                                message: `Xác nhận kết luận ứng viên "${selectedApplicantResults.applicant.name}" KHÔNG ĐẠT phỏng vấn?\n\nSau khi xác nhận, kết luận này sẽ không thể thay đổi.`,
                                onConfirm: () => {
                                  setConfirmDialog(null);
                                  handleKetLuan(
                                    selectedApplicantResults.applicant.id,
                                    4,
                                    'Không đạt phỏng vấn'
                                  );
                                },
                                onCancel: () => {
                                  setConfirmDialog(null);
                                }
                              });
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                            Kết luận: Không đạt
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}

      {experiencePopup && (
        <div className="experience-popup-overlay" onClick={() => setExperiencePopup(null)}>
          <div className="experience-popup" onClick={(e) => e.stopPropagation()}>
            <div className="experience-popup-header">
              <h4>Tổng quan ứng viên — {experiencePopup.name}</h4>
              <button className="experience-popup-close" onClick={() => setExperiencePopup(null)}>&times;</button>
            </div>
            <div className="experience-popup-body">
              <div className="overview-section">
                <div className="overview-label">Kinh nghiệm làm việc</div>
                <div className="overview-value">{experiencePopup.experience || 'Chưa có'}</div>
              </div>
              <div className="overview-section">
                <div className="overview-label">Kỹ năng chuyên môn</div>
                <div className="overview-value">{experiencePopup.skills || 'Chưa có'}</div>
              </div>
              {experiencePopup.softSkills && (
                <div className="overview-section">
                  <div className="overview-label">Kỹ năng mềm / Sở thích</div>
                  <div className="overview-value">{experiencePopup.softSkills}</div>
                </div>
              )}
              {experiencePopup.careerExpectation && (
                <div className="overview-section">
                  <div className="overview-label">Nguyện vọng</div>
                  <div className="overview-value">{experiencePopup.careerExpectation}</div>
                </div>
              )}
              {(experiencePopup.licenseNumber || experiencePopup.licenseIssueDate || experiencePopup.licensePlace || experiencePopup.licenseScope) && (
                <div className="overview-section">
                  <div className="overview-label">Giấy phép hành nghề</div>
                  <div className="overview-value">
                    {experiencePopup.licenseNumber && <div>Số GPHN: <strong>{experiencePopup.licenseNumber}</strong></div>}
                    {experiencePopup.licenseIssueDate && <div>Ngày cấp: {experiencePopup.licenseIssueDate}</div>}
                    {experiencePopup.licensePlace && <div>Nơi cấp: {experiencePopup.licensePlace}</div>}
                    {experiencePopup.licenseScope && <div>Phạm vi hoạt động: {experiencePopup.licenseScope}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetail;

